import { Spin, Image, Alert } from 'src/design-system/components/atoms';
import React, { useState, useEffect } from 'react';
import { sanitizeUrl } from '@utils/urlSanitizer';
import PdfRenderer from 'src/design-system/components/molecules/PdfRenderer/PdfRenderer';
import { useTheme } from 'src/theming/useTheme';

interface IProps {
  image: any;
}

const RenderImage = (props: IProps) => {
  const { image } = props;
  const theme = useTheme();
  const isFileObject = !image.url && image.name;

  let fileURL: string | null;
  let isValid: boolean;

  if (isFileObject) {
    // Local files from user input are trusted, so we bypass sanitization for blob URLs.
    // Blob URLs created by URL.createObjectURL() are safe from URL-based XSS attacks.
    fileURL = URL.createObjectURL(image);
    isValid = true;
  } else {
    // Remote URLs need protocol validation to prevent XSS attacks
    const result = sanitizeUrl(image.url);
    isValid = result.isValid;
    fileURL = result.isValid ? result.sanitizedUrl : null;
  }

  const [fileType, setFileType] = useState(image.type || null);
  const [isLoading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const fetchFileType = async () => {
    if (!fileType && fileURL && isValid) {
      try {
        setLoading(true);
        const response = await fetch(fileURL, { method: 'HEAD' });
        const contentType = response?.headers?.get('Content-Type');
        setFileType(contentType && contentType.includes('pdf') ? 'pdf' : 'image');
      } catch (error) {
        setFileType('image');
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchFileType();
  }, [fileURL, fileType]);

  const handleError = () => {
    setHasError(true);
  };

  if (isLoading) {
    return <Spin style={{ marginLeft: '50%' }} />;
  }

  return (
    <div style={{ background: theme.color.semantic.background.neutral.strong }}>
      {!isValid || hasError ? (
        <Alert
          message="Failed to load file"
          description="This file may be invalid or inaccessible"
          type="error"
          showIcon
        />
      ) : (
        <>
          {fileType && fileType.includes('pdf') ? (
            <PdfRenderer
              url={fileURL as string}
              onError={handleError}
            />
          ) : (
            <Image
              src={fileURL}
              placeholder={true}
              onError={handleError}
            />
          )}
        </>
      )}
    </div>
  );
};

export default RenderImage;
